import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { Box, Button, CircularProgress } from '@mui/material'

import { DefaultElementProps } from 'common/interfaces'
import { mainButtonStyle } from 'common/styles'
import { SizingContainer } from 'components/SizingContainer'
import InlineBackButton from 'components/StepContainer/InlineBackButton'
import { useHeapDispatch } from 'context/Heap'
import { useSessionDispatch, useSessionState } from 'context/Session'
import { useFlow, useFlowSlug } from 'utils/flow'
import { useLocalStorage } from 'utils/hook'

import { SubmitButton as SubmitButtonProps } from './interface'

const SubmitButton = ({
  text,
  onClick,
  variant = 'contained',
  allowMultipleClicks = false,
  clearOnSubmit = false,
}: SubmitButtonProps & DefaultElementProps): JSX.Element => {
  const { reset } = useFormContext()
  const flowSlug = useFlowSlug()
  const [, , clearForm] = useLocalStorage(`${flowSlug}/form`, {})
  const { isSubmitting, isSubmitted, isSubmitDisabled } = useSessionState()
  const [sessionDispatch] = useSessionDispatch()
  const [heapDispatch] = useHeapDispatch()
  const { data: flow } = useFlow()
  const inlineBack = flow?.back_button_inline === true
  const shouldDisable = isSubmitting || (!allowMultipleClicks && (isSubmitted || isSubmitDisabled))

  // Track previous isSubmitted value to detect transitions
  const prevIsSubmittedRef = useRef(isSubmitted)

  // Clear form whenever isSubmitted transitions from false to true
  useEffect(() => {
    // Detect transition: was false, now is true
    if (clearOnSubmit && !prevIsSubmittedRef.current && isSubmitted) {
      reset()
      clearForm()
    }

    // Update the ref to current value for next comparison
    prevIsSubmittedRef.current = isSubmitted
  }, [isSubmitted, clearOnSubmit, reset, clearForm])

  const handleClick = (): void => {
    if (allowMultipleClicks) {
      sessionDispatch({ type: 'resetSubmit' })
      heapDispatch({ type: 'add', fields: { _documentSubmitted: undefined } })
    }

    onClick?.(
      {},
      {
        onError: (e) => {
          console.log(e.message)
        },
      }
    )
  }

  return (
    <SizingContainer className="submit-btn-wrap">
      <Box sx={inlineBack ? mainButtonStyle.inlineContainer : mainButtonStyle.container}>
        <InlineBackButton />
        <Button
          form="form"
          variant={variant}
          onClick={handleClick}
          type="submit"
          disabled={shouldDisable}
          aria-busy={isSubmitting}
          sx={inlineBack ? mainButtonStyle.inlineButton : mainButtonStyle.button}
        >
          {Boolean(isSubmitting) && (
            <CircularProgress
              aria-hidden
              sx={{ marginRight: '12px', color: 'primary.dark' }}
              size={24}
            />
          )}
          {text}
        </Button>
      </Box>
    </SizingContainer>
  )
}

export default SubmitButton
